import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { OrdersApiAdapter } from '../adapters';
import { translateOrderApiError } from '../utils/errorTranslator';
import type { CreateOrderRequest, CreateOrderResponse } from '../adapters';

export const useCreateOrder = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('createEditOrdersModule');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createOrder = async (
    orderData: CreateOrderRequest
  ): Promise<CreateOrderResponse> => {
    setLoading(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const ordersAdapter = new OrdersApiAdapter(httpClient);

      const result = await ordersAdapter.createOrder(orderData);

      toast({
        title: t('toasts.success.title', 'Éxito'),
        description: result.message || t('toasts.success.createOrder', 'Orden creada exitosamente'),
        variant: 'default',
      });

      // Invalidar la caché de órdenes para actualizar la tabla
      await queryClient.invalidateQueries({ queryKey: ['orders'] });

      // Redirigir a la lista de órdenes
      navigate('/orders');

      return result;
    } catch (error: any) {
      // Traducir el error usando el nuevo sistema
      const translatedError = translateOrderApiError(error, t);

      toast({
        title: t('toasts.error.title', 'Error'),
        description: translatedError,
        variant: 'destructive',
      });

      return {
        success: false,
        message: translatedError,
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    createOrder,
    loading,
  };
};
